import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Navbar from '../components/Navbar';
import CitySelect from '../components/CitySelect';
import { Megaphone, Check, ArrowRight, ShieldCheck, Users, TrendingUp, Sparkles, CheckCircle, Upload, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import RazorpayPayButton from '../components/RazorpayPayButton';

const AdvertisePage = () => {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({
    advertiser_name: '', advertiser_email: '', advertiser_phone: '', advertiser_city: '', company_website: '',
    headline: '', body: '', cta_text: 'Learn More', cta_url: '', image_url: '',
    bg_color: '#FFFFFF', duration_months: 1, notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Only image files (PNG/JPG/WEBP)'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image too large (max 5 MB)'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/advertisements/upload-image`, fd);
      setForm((f) => ({ ...f, image_url: res.data.image_url }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  useEffect(() => {
    axios.get(`${API}/advertisements/catalog`).then((r) => {
      setCatalog(r.data.slots || []);
      if (r.data.slots?.length) setSelectedSlot(r.data.slots[0]);
    });
  }, []);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) return;
    const required = ['advertiser_name', 'advertiser_email', 'advertiser_phone', 'headline', 'cta_text', 'cta_url'];
    for (const k of required) {
      if (!form[k]) { toast.error(`Missing: ${k.replace('_', ' ')}`); return; }
    }
    if (!/^\d{10}$/.test(form.advertiser_phone)) { toast.error('Phone must be 10 digits'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/advertisements`, {
        ...form,
        duration_months: Number(form.duration_months) || 1,
        slot: selectedSlot.key,
      });
      setDone(res.data);
      toast.success('Booking received! Our team will contact you within 24 hours.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const monthly = selectedSlot?.monthly_price || 0;
  const duration = Number(form.duration_months) || 1;
  const total = monthly * duration;

  if (done) {
    return (
      <div style={{ background: 'white', minHeight: '100vh' }}>
        <Navbar />
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '5rem 1.5rem' }}>
          <div className="calculator-section" style={{ textAlign: 'center', padding: '3rem 2rem' }} data-testid="ad-success">
            <CheckCircle size={72} style={{ color: '#10B981', margin: '0 auto 1.25rem' }} />
            <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.25rem)', color: '#0F172A', marginBottom: '0.5rem' }}>Booking Received</h2>
            <p style={{ color: '#475569', marginBottom: '1rem' }}>
              <strong>Reference:</strong> <span style={{ fontFamily: 'monospace', color: '#FF6B35', fontWeight: 700, fontSize: '1.15rem' }}>{done.id.slice(0, 8)}</span>
            </p>
            <p style={{ color: '#475569', marginBottom: '2rem' }}>
              Our team will email <strong>{done.advertiser_email}</strong> within 24 hours with payment details. Total: <strong style={{ color: '#FF6B35' }}>₹{Number(done.total_amount).toLocaleString('en-IN')}</strong>
            </p>

            <div style={{ background: 'linear-gradient(135deg, #FFF7ED 0%, #FED7AA 100%)', borderRadius: 14, padding: '1.5rem', marginBottom: '1.75rem', textAlign: 'left' }} data-testid="ad-pay-block">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <Sparkles size={18} style={{ color: '#9A3412' }} />
                <h3 style={{ color: '#9A3412', margin: 0, fontSize: '1rem', fontWeight: 800 }}>Skip the wait — pay now to go live faster</h3>
              </div>
              <p style={{ color: '#9A3412', fontSize: '0.85rem', margin: '0 0 1rem 0' }}>
                Pay securely via Razorpay. UPI, cards, net-banking & wallets accepted. Your ad enters review immediately on payment.
              </p>
              <RazorpayPayButton
                entityType="ad_booking"
                entityId={done.id}
                amountInr={done.total_amount}
                prefill={{ name: done.advertiser_name, email: done.advertiser_email, contact: done.advertiser_phone }}
                label={`Pay ₹${Number(done.total_amount).toLocaleString('en-IN')} now`}
                onPaid={() => toast.success('Ad payment received — review in progress', { duration: 4000 })}
              />
            </div>

            <button onClick={() => navigate('/')} className="outline-button" data-testid="ad-success-home">Back to Home</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#FFFFFF', minHeight: '100vh' }} data-testid="advertise-page">
      <Navbar />

      {/* Hero */}
      <section style={{ background: 'linear-gradient(135deg, #FFF7ED 0%, #FFFFFF 50%, #EFF6FF 100%)', padding: '4rem 1.5rem 3rem' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.4rem 0.9rem', background: '#FF6B35', color: 'white', borderRadius: 999, fontSize: '0.78rem', fontWeight: 700, marginBottom: '1.25rem' }}>
            <Megaphone size={14} /> ADVERTISE WITH ARTHIK
          </div>
          <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3.25rem)', color: '#0F172A', margin: 0, lineHeight: 1.15, fontWeight: 800 }}>
            Reach <span style={{ color: '#FF6B35' }}>10,000+ Indians</span> looking for loans, tax & investment products
          </h1>
          <p style={{ fontSize: '1.1rem', color: '#475569', marginTop: '1rem', maxWidth: 720, marginLeft: 'auto', marginRight: 'auto' }}>
            Get your brand in front of high-intent financial decision-makers. Transparent pricing. Monthly contracts. No hidden fees.
          </p>
          <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', marginTop: '2rem', flexWrap: 'wrap' }}>
            <Stat icon={Users} value="10K+" label="Monthly visitors" />
            <Stat icon={TrendingUp} value="65%" label="Tier-1 city audience" />
            <Stat icon={ShieldCheck} value="100%" label="Money-back guarantee" />
          </div>
        </div>
      </section>

      {/* Slot pricing */}
      <section style={{ padding: '3rem 1.5rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.25rem)', color: '#0F172A', marginBottom: '0.5rem' }}>Pick your ad slot</h2>
          <p style={{ color: '#475569', marginBottom: '2rem' }}>5 high-visibility placements. Pay monthly. Cancel anytime.</p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem', marginBottom: '3rem' }} data-testid="ad-slot-grid">
            {catalog.map((s) => {
              const active = selectedSlot?.key === s.key;
              return (
                <div
                  key={s.key}
                  onClick={() => setSelectedSlot(s)}
                  data-testid={`ad-slot-${s.key}`}
                  style={{
                    padding: '1.5rem',
                    border: active ? '2px solid #FF6B35' : '2px solid #E2E8F0',
                    borderRadius: '1rem',
                    cursor: 'pointer',
                    background: active ? '#FFF7ED' : 'white',
                    transition: 'all 0.2s ease',
                    boxShadow: active ? '0 8px 24px rgba(255,107,53,0.2)' : 'none',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <h3 style={{ fontSize: '1rem', color: '#0F172A', margin: 0, fontWeight: 700 }}>{s.label}</h3>
                    {active && <Check size={18} style={{ color: '#FF6B35' }} />}
                  </div>
                  <p style={{ color: '#64748B', fontSize: '0.8rem', margin: '0.25rem 0 0.75rem' }}>{s.description}</p>
                  <div style={{ fontSize: '0.7rem', color: '#94A3B8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' }}>Size: {s.size}</div>
                  <div style={{ fontSize: '1.5rem', color: '#FF6B35', fontWeight: 800, marginTop: '0.5rem' }}>₹{s.monthly_price.toLocaleString('en-IN')}<span style={{ fontSize: '0.75rem', color: '#94A3B8', fontWeight: 500 }}>/mo</span></div>
                </div>
              );
            })}
          </div>

          {/* Booking form */}
          <div className="calculator-section" style={{ padding: '2rem' }}>
            <h3 style={{ fontSize: '1.25rem', color: '#0F172A', marginBottom: '0.25rem' }}>Book your ad — {selectedSlot?.label || ''}</h3>
            <p style={{ color: '#64748B', fontSize: '0.875rem', marginBottom: '1.5rem' }}>Tell us about your business and your ad creative. We'll reach out within 24 hours.</p>

            <form onSubmit={submit} data-testid="ad-booking-form">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.85rem' }}>
                <Field label="Your name *" v={form.advertiser_name} onChange={handle('advertiser_name')} testId="adv-name" />
                <Field label="Email *" type="email" v={form.advertiser_email} onChange={handle('advertiser_email')} testId="adv-email" />
                <Field label="Phone (10 digits) *" v={form.advertiser_phone} onChange={handle('advertiser_phone')} maxLength={10} testId="adv-phone" />
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '0.35rem' }}>City</label>
                  <CitySelect value={form.advertiser_city} onChange={(v) => setForm({ ...form, advertiser_city: v })} testId="adv-city" />
                </div>
                <Field label="Company website" v={form.company_website} onChange={handle('company_website')} placeholder="https://example.com" testId="adv-website" />
              </div>

              <div style={{ marginTop: '1.25rem', padding: '1rem', background: '#F8FAFC', borderRadius: 10 }}>
                <h4 style={{ fontSize: '0.85rem', color: '#0F172A', margin: '0 0 0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>📢 Your ad creative</h4>
                <div style={{ display: 'grid', gap: '0.85rem' }}>
                  <Field label="Ad headline *" v={form.headline} onChange={handle('headline')} placeholder="e.g., Open a free Demat account in 5 minutes" testId="adv-headline" />
                  <div>
                    <label className="label-text">Ad body (optional)</label>
                    <textarea className="input-field" rows={2} value={form.body} onChange={handle('body')} placeholder="Short supporting sentence" data-testid="adv-body" />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.85rem' }}>
                    <Field label="CTA button text *" v={form.cta_text} onChange={handle('cta_text')} testId="adv-cta-text" />
                    <Field label="CTA URL *" v={form.cta_url} onChange={handle('cta_url')} placeholder="https://yoursite.com/landing" testId="adv-cta-url" />
                  </div>
                  <Field label="Image URL (optional)" v={form.image_url} onChange={handle('image_url')} placeholder="https://yoursite.com/banner.jpg" testId="adv-image" />

                  <div>
                    <label className="label-text">Ad image — Upload or paste URL above (optional)</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <label
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.55rem 1rem', background: '#FF6B35', color: 'white', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem', cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.6 : 1 }}
                        data-testid="adv-image-upload-label"
                      >
                        {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Upload size={14} /> Upload image</>}
                        <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp" onChange={handleImageUpload} style={{ display: 'none' }} disabled={uploading} data-testid="adv-image-file" />
                      </label>
                      <span style={{ fontSize: '0.75rem', color: '#94A3B8' }}>PNG, JPG, WEBP • max 5 MB</span>
                    </div>
                    {form.image_url && (
                      <div style={{ marginTop: 12, padding: '0.6rem', background: '#F8FAFC', borderRadius: 10, border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 12 }} data-testid="adv-image-preview">
                        <img src={form.image_url} alt="Ad preview" style={{ maxWidth: 140, maxHeight: 80, borderRadius: 6, border: '1px solid #E2E8F0' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.78rem', color: '#10B981', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={12} /> Image ready</div>
                          <div style={{ fontSize: '0.7rem', color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{form.image_url}</div>
                        </div>
                        <button type="button" onClick={() => setForm({ ...form, image_url: '' })} style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#EF4444', width: 28, height: 28, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Remove" data-testid="adv-image-remove"><X size={14} /></button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '1.25rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
                <div>
                  <label className="label-text">Duration (months)</label>
                  <select className="input-field" value={form.duration_months} onChange={handle('duration_months')} data-testid="adv-duration">
                    {[1, 2, 3, 6, 12].map((m) => <option key={m} value={m}>{m} {m === 1 ? 'month' : 'months'}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <div style={{ background: 'linear-gradient(135deg, #FFF7ED 0%, #FED7AA 100%)', borderRadius: 10, padding: '0.75rem 1rem', width: '100%' }}>
                    <div style={{ fontSize: '0.7rem', color: '#9A3412', fontWeight: 700, textTransform: 'uppercase' }}>Total</div>
                    <div style={{ fontSize: '1.5rem', color: '#FF6B35', fontWeight: 800 }} data-testid="adv-total">₹{total.toLocaleString('en-IN')}</div>
                  </div>
                </div>
              </div>

              <button type="submit" disabled={submitting} className="primary-button" style={{ marginTop: '1.5rem', width: '100%', justifyContent: 'center', padding: '0.95rem' }} data-testid="adv-submit">
                <Sparkles size={18} /> {submitting ? 'Submitting…' : 'Book My Ad'} <ArrowRight size={16} />
              </button>
              <p style={{ marginTop: '0.75rem', color: '#94A3B8', fontSize: '0.75rem', textAlign: 'center' }}>
                Submit your booking — pay securely via Razorpay on the next screen or wait for our team to invoice you.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, v, onChange, type = 'text', placeholder = '', maxLength, testId }) => (
  <div>
    <label className="label-text">{label}</label>
    <input className="input-field" type={type} value={v} onChange={onChange} placeholder={placeholder} maxLength={maxLength} data-testid={testId} />
  </div>
);

const Stat = ({ icon: Icon, value, label }) => (
  <div style={{ background: 'white', padding: '0.85rem 1.25rem', borderRadius: 12, border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 2px 8px rgba(15,23,42,0.04)' }}>
    <Icon size={22} style={{ color: '#FF6B35' }} />
    <div>
      <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0F172A', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '0.75rem', color: '#64748B' }}>{label}</div>
    </div>
  </div>
);

export default AdvertisePage;
